import { Injectable } from '@angular/core';

import { Observable, Subject } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';

import { Feature } from 'src/app/features/feature-management/models';
import { FeatureManagementService } from 'src/app/features/feature-management/services';

import {
  AvTelemetrySink,
  AvTelemetrySource,
} from '../models/av-telemetry-source';
import {
  AV_TELEMETRY_POLL_INTERVAL_MS,
  AV_TELEMETRY_SCHEMA_VERSION,
  AvTelemetryEnvelope,
  AvTelemetryRecordType,
} from '../models/av-telemetry.model';

@Injectable({ providedIn: 'root' })
export class AvTelemetrySamplerService implements AvTelemetrySink {
  static readonly MAX_BUFFERED_RECORDS = 5_000;

  readonly isEnabled$: Observable<boolean>;

  private readonly eventReported = new Subject<string>();

  /** Discrete event names as they are recorded, so the uploader can flush on a severe one. */
  readonly eventReported$: Observable<string> = this.eventReported.asObservable();

  private enabled = false;
  private source: AvTelemetrySource | null = null;
  private records: AvTelemetryEnvelope[] = [];
  private seq = 0;
  private droppedRecords = 0;
  private pollIntervalId: ReturnType<typeof setInterval> | null = null;

  constructor(private readonly featureManagementService: FeatureManagementService) {
    this.isEnabled$ = this.featureManagementService
      .isFeatureEnabled$(Feature.AvTelemetry)
      .pipe(
        tap((isEnabled: boolean) => (this.enabled = isEnabled)),
        shareReplay({ bufferSize: 1, refCount: false })
      );
  }

  get isEnabled(): boolean {
    return this.enabled;
  }

  get isSampling(): boolean {
    return this.source !== null;
  }

  get bufferedRecordCount(): number {
    return this.records.length;
  }

  get droppedRecordCount(): number {
    return this.droppedRecords;
  }

  start(source: AvTelemetrySource, firstSeq = 0): void {
    if (!this.enabled || this.source) {
      return;
    }

    this.source = source;
    this.seq = firstSeq;
    this.droppedRecords = 0;
    this.records = [];

    this.append('meta', source.describeClient());
    source.attach(this);

    if (source.cadenceMode === 'poll') {
      this.startPolling(source);
    }
  }

  stop(): void {
    this.stopPolling();

    if (this.source) {
      this.source.detach();
      this.source = null;
    }
  }

  drain(): AvTelemetryEnvelope[] {
    const drained = this.records;
    this.records = [];
    return drained;
  }

  peek(): readonly AvTelemetryEnvelope[] {
    return this.records;
  }

  reportTick(payload: unknown): void {
    this.append('tick', payload);
  }

  reportEvent(payload: unknown): void {
    this.append('event', payload);

    const name = (payload as { name?: string })?.name;
    if (name) {
      this.eventReported.next(name);
    }
  }

  private startPolling(source: AvTelemetrySource): void {
    if (!source.sampleTick) {
      return;
    }

    this.pollIntervalId = setInterval(() => {
      const sample = source.sampleTick?.();
      if (sample !== null && sample !== undefined) {
        this.reportTick(sample);
      }
    }, AV_TELEMETRY_POLL_INTERVAL_MS);
  }

  private stopPolling(): void {
    if (this.pollIntervalId !== null) {
      clearInterval(this.pollIntervalId);
      this.pollIntervalId = null;
    }
  }

  private append(type: AvTelemetryRecordType, payload: unknown): void {
    if (!this.enabled || !this.source) {
      return;
    }

    // An empty sessionKey (e.g. Chime's meetingId not yet hydrated) is a guaranteed 400 at
    // ingest, and the uploader retries 400s forever — never buffer a record it can't carry.
    if (!this.source.sessionKey) {
      return;
    }

    this.evictIfFull();

    const seq = this.seq;
    this.seq++;

    this.records.push({
      type: type,
      schemaVersion: AV_TELEMETRY_SCHEMA_VERSION,
      occurredAt: Date.now(),
      vendor: this.source.vendor,
      vendorVersion: this.source.vendorVersion,
      seq: seq,
      sessionKey: this.source.sessionKey,
      payload: payload,
    });
  }

  private evictIfFull(): void {
    if (this.records.length < AvTelemetrySamplerService.MAX_BUFFERED_RECORDS) {
      return;
    }

    const oldestTickIndex = this.records.findIndex((record) => record.type === 'tick');
    this.records.splice(oldestTickIndex >= 0 ? oldestTickIndex : 0, 1);
    this.droppedRecords++;
  }
}
